/* Wavian chapters — shared runtime for the homepage.
   - Reveal-on-scroll for anything with [data-reveal]
   - Show/hide the floating shop pill based on hero + outro visibility
   - Chapter-local IntersectionObserver hooks for tab-, dropdown-, and
     carousel-based chapters (chapter files may add data attrs).
   No libraries. All CSS-driven; JS just flips data attrs. */

(function () {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ----- Reveal on scroll ------------------------------------------------
  const revealTargets = document.querySelectorAll('[data-reveal]');
  if (revealTargets.length) {
    try {
      if (reducedMotion || !('IntersectionObserver' in window)) {
        revealTargets.forEach((el) => el.setAttribute('data-visible', 'true'));
      } else {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.setAttribute('data-visible', 'true');
              io.unobserve(entry.target);
            }
          });
        }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 });
        revealTargets.forEach((el) => io.observe(el));
      }
    } catch (e) {
      // Never leave content stuck at opacity:0 if the observer setup fails.
      revealTargets.forEach((el) => el.setAttribute('data-visible', 'true'));
    }
  }

  // ----- Floating shop pill ---------------------------------------------
  const pill = document.querySelector('[data-wv-shop-pill]');
  const heroSentinel = document.querySelector('[data-wv-hero-end]');
  const outroSentinel = document.querySelector('[data-wv-outro-start]');

  if (pill && heroSentinel) {
    let heroPassed = false;
    let outroReached = false;
    let ctaVisible = false;
    const update = () => {
      const shouldShow = heroPassed && !outroReached && !ctaVisible;
      pill.setAttribute('data-visible', shouldShow ? 'true' : 'false');
    };
    const heroIo = new IntersectionObserver((entries) => {
      entries.forEach((e) => { heroPassed = !e.isIntersecting && e.boundingClientRect.top < 0; update(); });
    }, { threshold: 0 });
    heroIo.observe(heroSentinel);

    if (outroSentinel) {
      const outroIo = new IntersectionObserver((entries) => {
        entries.forEach((e) => { outroReached = e.isIntersecting; update(); });
      }, { threshold: 0.1 });
      outroIo.observe(outroSentinel);
    }

    // Hide the pill whenever any in-page primary CTA row is on screen —
    // the page's own CTAs supersede the sticky one and prevent overlap.
    const ctaRows = document.querySelectorAll('[data-wv-cta-row]');
    if (ctaRows.length) {
      const visibleRows = new Set();
      const ctaIo = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) visibleRows.add(e.target);
          else visibleRows.delete(e.target);
        });
        ctaVisible = visibleRows.size > 0;
        update();
      }, { threshold: 0.15 });
      ctaRows.forEach((row) => ctaIo.observe(row));
    }
  }

  // ----- Chapter tabs (data-wv-tabs) ------------------------------------
  document.querySelectorAll('[data-wv-tabs]').forEach((tabsRoot) => {
    const tabs = Array.from(tabsRoot.querySelectorAll('[data-wv-tab]'));
    const panels = tabsRoot.querySelectorAll('[data-wv-tab-panel]');
    if (!tabs.length) return;

    const setActive = (key, focusTab) => {
      tabs.forEach((t) => {
        const selected = t.dataset.wvTab === key;
        t.setAttribute('aria-selected', selected ? 'true' : 'false');
        t.setAttribute('tabindex', selected ? '0' : '-1');
        if (selected && focusTab) t.focus();
      });
      panels.forEach((p) => p.setAttribute('data-active', p.dataset.wvTabPanel === key ? 'true' : 'false'));
    };

    tabs.forEach((t, i) => {
      t.addEventListener('click', () => setActive(t.dataset.wvTab));
      t.addEventListener('keydown', (e) => {
        let target = null;
        switch (e.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            target = tabs[(i + 1) % tabs.length];
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            target = tabs[(i - 1 + tabs.length) % tabs.length];
            break;
          case 'Home':
            target = tabs[0];
            break;
          case 'End':
            target = tabs[tabs.length - 1];
            break;
          default:
            return;
        }
        e.preventDefault();
        setActive(target.dataset.wvTab, true);
      });
    });
    setActive(tabs[0].dataset.wvTab);
  });

  // ----- Highlights carousel (data-wv-carousel) -------------------------
  document.querySelectorAll('[data-wv-carousel]').forEach((root) => {
    const track = root.querySelector('[data-wv-carousel-track]');
    const slides = root.querySelectorAll('[data-wv-carousel-slide]');
    const dots = root.querySelectorAll('[data-wv-carousel-dot]');
    const prev = root.querySelector('[data-wv-carousel-prev]');
    const next = root.querySelector('[data-wv-carousel-next]');
    const playPause = root.querySelector('[data-wv-carousel-playpause]');
    if (!track || slides.length === 0) return;

    let index = 0;
    let auto = null;
    const autoMs = parseInt(root.dataset.wvCarouselAutoMs || '0', 10);

    const scrollToIndex = (i) => {
      index = (i + slides.length) % slides.length;
      const target = slides[index];
      if (target) {
        // Scroll ONLY the horizontal track — never the page. scrollIntoView
        // (even with block:'nearest') vertically drags the document back to
        // the carousel when autoplay fires while the user is elsewhere.
        const left = target.offsetLeft - (track.clientWidth - target.clientWidth) / 2;
        track.scrollTo({ left, behavior: reducedMotion ? 'auto' : 'smooth' });
      }
      dots.forEach((d, di) => d.setAttribute('aria-current', di === index ? 'true' : 'false'));
    };

    if (prev) prev.addEventListener('click', () => scrollToIndex(index - 1));
    if (next) next.addEventListener('click', () => scrollToIndex(index + 1));
    dots.forEach((d, di) => d.addEventListener('click', () => scrollToIndex(di)));

    if (autoMs > 0 && !reducedMotion) {
      // Autoplay only while the carousel is on screen and the user hasn't
      // paused it (WCAG 2.2.2: any moving content needs a pause control).
      let inView = false;
      let userPaused = false;
      const startAuto = () => { if (!auto && inView && !userPaused) auto = setInterval(() => scrollToIndex(index + 1), autoMs); };
      const stopAuto = () => { if (auto) { clearInterval(auto); auto = null; } };
      root.addEventListener('mouseenter', stopAuto);
      root.addEventListener('mouseleave', startAuto);
      // Pause while keyboard focus is inside the carousel, resume on leave.
      root.addEventListener('focusin', stopAuto);
      root.addEventListener('focusout', (e) => { if (!root.contains(e.relatedTarget)) startAuto(); });

      if (playPause) {
        playPause.addEventListener('click', () => {
          userPaused = !userPaused;
          playPause.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
          playPause.setAttribute('aria-label', userPaused ? 'Play automatic slideshow' : 'Pause automatic slideshow');
          if (userPaused) stopAuto(); else startAuto();
        });
      }

      if ('IntersectionObserver' in window) {
        const autoIo = new IntersectionObserver((entries) => {
          entries.forEach((e) => { inView = e.isIntersecting; if (inView) startAuto(); else stopAuto(); });
        }, { threshold: 0.35 });
        autoIo.observe(root);
      } else {
        inView = true;
        startAuto();
      }
    } else if (playPause) {
      // No autoplay running (disabled or reduced motion): hide the control.
      playPause.hidden = true;
    }

    // Sync active dot when user manually scrolls
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const slideIndex = Array.from(slides).indexOf(e.target);
            if (slideIndex >= 0) {
              index = slideIndex;
              dots.forEach((d, di) => d.setAttribute('aria-current', di === index ? 'true' : 'false'));
            }
          }
        });
      }, { root: track, threshold: 0.6 });
      slides.forEach((s) => io.observe(s));
    }
  });

  // ----- Comparison dropdown (data-wv-compare) --------------------------
  document.querySelectorAll('[data-wv-compare]').forEach((root) => {
    const select = root.querySelector('[data-wv-compare-select]');
    const panels = root.querySelectorAll('[data-wv-compare-panel]');
    if (!select) return;
    const setActive = (key) => {
      panels.forEach((p) => p.setAttribute('data-active', p.dataset.wvComparePanel === key ? 'true' : 'false'));
    };
    select.addEventListener('change', () => setActive(select.value));
    if (select.value) setActive(select.value);
  });

  // ----- Preload deferred images (data-wv-preload) ---------------------
  // Lazy images inside opacity-0 tab panels — or in horizontally off-screen
  // carousel cards — never load until interacted with, leaving a black media
  // box. Force-fetch them shortly after load so they're ready in place.
  const preloadPanels = () => {
    document.querySelectorAll('img[data-wv-preload]').forEach((im) => {
      if (im.loading === 'lazy') im.loading = 'eager';
      const pre = new Image();
      if (im.srcset) pre.srcset = im.srcset;
      if (im.sizes) pre.sizes = im.sizes;
      pre.src = im.currentSrc || im.src;
    });
  };
  if ('requestIdleCallback' in window) {
    requestIdleCallback(preloadPanels, { timeout: 3000 });
  } else {
    window.setTimeout(preloadPanels, 1500);
  }
})();
